import { EFeatureNames, EWorkflowTypes } from '@albacross/types'

export enum CRM_OBJECT_TYPE {
  Company = 'company',
  Contact = 'contact',
  Deal = 'deal',
  Account = 'account',
  Lead = 'lead',
  CustomObject = 'customObject',
}

export const WORKFLOW_TYPES = {
  Email: 'email',
  Pipedrive: 'pipedrive',
  Slack: 'slack',
  Webhook: 'webhook',
  Clay: 'clay',
  Zapier: 'zapier',
  Hubspot: 'hubspot',
  Salesforce: 'salesforce',
  MicrosoftTeams: 'microsoft_teams',
  GoogleSheets: 'google_sheets',
  Linkedin: 'linkedin',
  Attio: 'attio',
  N8n: 'n8n',
}

export const WORKFLOW_COOKIES_NAMES = {
  Hubspot: 'workflow_hubspot',
  Pipedrive: 'workflow_pipedrive',
}

export const WORKFLOW_COOKIES = {
  workflow_hubspot: WORKFLOW_TYPES.Hubspot,
  workflow_pipedrive: WORKFLOW_TYPES.Pipedrive,
}

export const WORKFLOW_CUSTOM_FORMAT = {
  NONE: '',
  MICROSOFT_TEAMS: 'MicrosoftTeams',
  HANGOUT_CHAT: 'HangoutChat',
  MATTER_MOST: 'MatterMost',
}

export const COMPANY_FIELDS = {
  COMPANY_NAME: 'companyName',
  WORKFLOW_NAME: 'workflowName',
  COUNTRY: 'country',
  WEBSITE: 'website',
  EMPLOYEES: 'employees',
  REVENUE: 'revenue',
  ADDRESS: 'address',
  PHONE_NUMBER: 'phoneNumber',
  INDUSTRY: 'industry',
  NACE_INDUSTRY: 'naceIndustry',
  ACTIVITY: 'activity',
  UTM_TAGS: 'utmTags',
  GCLIDS: 'gclids',
  LINK: 'link',
  HRB: 'hrb',
  LINK_TO_SALESFORCE_ACCOUNT: 'salesforceLink',
  LINK_TO_HUBSPOT_COMPANY: 'hubspotLink',
  LINK_TO_LINKEDIN_COMPANY: 'linkedinLink',
  TAGS: 'tags',
}

export const REQUIRED_COMPANY_FIELDS = [
  COMPANY_FIELDS.COMPANY_NAME,
  COMPANY_FIELDS.WORKFLOW_NAME,
  COMPANY_FIELDS.COUNTRY,
  COMPANY_FIELDS.WEBSITE,
  COMPANY_FIELDS.EMPLOYEES,
  COMPANY_FIELDS.REVENUE,
  COMPANY_FIELDS.ADDRESS,
]

export const OPTIONAL_COMPANY_FIELDS = [
  COMPANY_FIELDS.PHONE_NUMBER,
  COMPANY_FIELDS.INDUSTRY,
  COMPANY_FIELDS.NACE_INDUSTRY,
  COMPANY_FIELDS.ACTIVITY,
  COMPANY_FIELDS.UTM_TAGS,
  COMPANY_FIELDS.GCLIDS,
  COMPANY_FIELDS.HRB,
  COMPANY_FIELDS.TAGS,
]

export const DEFAULT_COMPANY_FIELDS = [COMPANY_FIELDS.PHONE_NUMBER, COMPANY_FIELDS.INDUSTRY, COMPANY_FIELDS.LINK]

export const OPTIONAL_COMPANY_LINK_FIELDS = [
  COMPANY_FIELDS.LINK,
  COMPANY_FIELDS.LINK_TO_SALESFORCE_ACCOUNT,
  COMPANY_FIELDS.LINK_TO_HUBSPOT_COMPANY,
  COMPANY_FIELDS.LINK_TO_LINKEDIN_COMPANY,
]

export const MICROSOFT_TEAMS_DEFAULT_COMPANY_FIELDS = [COMPANY_FIELDS.PHONE_NUMBER, COMPANY_FIELDS.INDUSTRY]

export const WORKFLOW_TAGS = {
  All: 'all',
  Internal: 'internal',
  Other: 'other',
  Crm: 'crm',
  Marketing: 'marketing',
}

export const WORKFLOW_OPERATION_TYPE = {
  Add: 'add',
  Update: 'update',
  AddOrUpdate: 'add_or_update',
}

export const WORKFLOW_EMAIL_REPORT_TYPE = {
  CSV: 'csv',
  Default: 'default',
}

export const WORKFLOW_CONTACTS_COUNTRY_FILTER = {
  All: 'all',
  BasedOnLeadCountry: 'based_on_lead_country',
  Selected: 'selected',
}

export const WORKFLOW_TYPES_TAG = {
  [WORKFLOW_TYPES.Email]: WORKFLOW_TAGS.Internal,
  [WORKFLOW_TYPES.Zapier]: WORKFLOW_TAGS.Other,
  [WORKFLOW_TYPES.Webhook]: WORKFLOW_TAGS.Internal,
  [WORKFLOW_TYPES.Clay]: WORKFLOW_TAGS.Internal,
  [WORKFLOW_TYPES.Pipedrive]: WORKFLOW_TAGS.Crm,
  [WORKFLOW_TYPES.Slack]: WORKFLOW_TAGS.Other,
  [WORKFLOW_TYPES.Hubspot]: WORKFLOW_TAGS.Crm,
  [WORKFLOW_TYPES.Salesforce]: WORKFLOW_TAGS.Crm,
  [WORKFLOW_TYPES.MicrosoftTeams]: WORKFLOW_TAGS.Other,
  [WORKFLOW_TYPES.GoogleSheets]: WORKFLOW_TAGS.Other,
  [WORKFLOW_TYPES.Linkedin]: WORKFLOW_TAGS.Marketing,
  [WORKFLOW_TYPES.Attio]: WORKFLOW_TAGS.Crm,
}

export const WORKFLOW_PRINTABLE_NAMES = {
  [EWorkflowTypes.email]: 'Email Report',
  [EWorkflowTypes.zapier]: 'Zapier',
  [EWorkflowTypes.webhook]: 'Webhook',
  [EWorkflowTypes.clay]: 'Clay',
  [EWorkflowTypes.pipedrive]: 'Pipedrive',
  [EWorkflowTypes.slack]: 'Slack',
  [EWorkflowTypes.hubspot]: 'HubSpot',
  [EWorkflowTypes.salesforce]: 'Salesforce',
  [EWorkflowTypes.microsoftTeams]: 'Microsoft Teams',
  [EWorkflowTypes.googleSheets]: 'Google Sheets',
  [EWorkflowTypes.linkedin]: 'LinkedIn Ads',
  [EWorkflowTypes.outreach]: 'Auto-Engage',
  [EWorkflowTypes.attio]: 'Attio',
  [EWorkflowTypes.n8n]: 'n8n',
} as const

export const ALLOWED_EXPORT_WORKFLOW_TYPES = [
  WORKFLOW_TYPES.Hubspot,
  WORKFLOW_TYPES.Pipedrive,
  WORKFLOW_TYPES.Slack,
  WORKFLOW_TYPES.Webhook,
  WORKFLOW_TYPES.Clay,
  WORKFLOW_TYPES.Zapier,
  WORKFLOW_TYPES.Salesforce,
  WORKFLOW_TYPES.MicrosoftTeams,
  WORKFLOW_TYPES.GoogleSheets,
  WORKFLOW_TYPES.Linkedin,
  WORKFLOW_TYPES.Attio,
]

export const WORKFLOWS_BASE = {
  [WORKFLOW_TYPES.Email]: { features: [EFeatureNames.WorkflowEmail] },
  [WORKFLOW_TYPES.Linkedin]: { features: [EFeatureNames.WorkflowLinkedin] },
  [WORKFLOW_TYPES.Slack]: { features: [EFeatureNames.WorkflowSlack] },
  [WORKFLOW_TYPES.MicrosoftTeams]: { features: [EFeatureNames.WorkflowMicrosoftTeams] },
  [WORKFLOW_TYPES.Hubspot]: { features: [EFeatureNames.WorkflowHubspot] },
  [WORKFLOW_TYPES.Salesforce]: { features: [EFeatureNames.WorkflowSalesforce] },
  [WORKFLOW_TYPES.Pipedrive]: { features: [EFeatureNames.WorkflowPipedrive] },
  [WORKFLOW_TYPES.GoogleSheets]: { features: [EFeatureNames.WorkflowGoogleSheets] },
  [WORKFLOW_TYPES.Zapier]: { features: [EFeatureNames.WorkflowZapier] },
  [WORKFLOW_TYPES.Webhook]: { features: [EFeatureNames.WorkflowWebhook] },
  [WORKFLOW_TYPES.Clay]: { features: [EFeatureNames.WorkflowClay] },
  [WORKFLOW_TYPES.Attio]: { features: [EFeatureNames.WorkflowAttio] },
  [WORKFLOW_TYPES.N8n]: { features: [EFeatureNames.WorkflowN8n] },
}
